/**
 * Excel Import/Export Utilities
 * Uses SheetJS (xlsx) for Excel operations
 */
import * as XLSX from 'xlsx';
import { Epic, Project, EpicLane } from '../types';

// Template column definitions
export const TEMPLATE_COLUMNS = [
    'Title',
    'Project',
    'Lane',
    'Progress',
    'Security',
    'Business Value',
    'Time Criticality',
    'Ops Impact',
    'Confidence',
    'Effort',
    'SGM',
    'Strategic Context'
] as const;

// Valid lanes
const VALID_LANES: EpicLane[] = ['Unassigned', 'Now', 'Next', 'Later', 'Done'];
const VALID_EFFORTS = [1, 2, 3, 4, 5];

export interface ImportRow {
    title: string;
    projectName: string;
    lane: EpicLane;
    progress: number;
    security: number;
    business: number;
    time: number;
    ops: number;
    confidence: number;
    effort: 1 | 2 | 3 | 4 | 5;
    sgm: boolean;
    notes: string;
}

export interface ValidationError {
    row: number;
    column: string;
    message: string;
}

export interface ParsedImportResult {
    rows: ImportRow[];
    errors: ValidationError[];
    isValid: boolean;
}

/**
 * Generate and download the import template
 */
export function downloadTemplate(): void {
    const sampleRow = {
        'Title': 'Example Epic',
        'Project': 'My Project',
        'Lane': 'Unassigned',
        'Progress': 0,
        'Security': 3,
        'Business Value': 3,
        'Time Criticality': 3,
        'Ops Impact': 3,
        'Confidence': 3,
        'Effort': 3,
        'SGM': 'FALSE',
        'Strategic Context': 'Brief description of the strategic goals'
    };

    const ws = XLSX.utils.json_to_sheet([sampleRow], { header: [...TEMPLATE_COLUMNS] });

    // Set column widths
    ws['!cols'] = [
        { wch: 30 }, // Title
        { wch: 20 }, // Project
        { wch: 12 }, // Lane
        { wch: 10 }, // Progress
        { wch: 10 }, // Security
        { wch: 15 }, // Business Value
        { wch: 15 }, // Time Criticality
        { wch: 12 }, // Ops Impact
        { wch: 12 }, // Confidence
        { wch: 10 }, // Effort
        { wch: 8 },  // SGM
        { wch: 40 }, // Strategic Context
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Epics');
    XLSX.writeFile(wb, 'epic_import_template.xlsx');
}

/**
 * Export existing epics to Excel format
 */
export function exportEpics(epics: Epic[], projects: Project[]): void {
    const getProjectName = (projectId: string) => projects.find(p => p.id === projectId)?.name || 'Unknown';

    const data = epics.map(epic => ({
        'Title': epic.title,
        'Project': getProjectName(epic.projectId),
        'Lane': epic.lane,
        'Progress': epic.progress ?? 0,
        'Security': epic.security ?? 3,
        'Business Value': epic.business ?? 3,
        'Time Criticality': epic.time ?? 3,
        'Ops Impact': epic.ops ?? 3,
        'Confidence': epic.confidence ?? 3,
        'Effort': epic.effort ?? 3,
        'SGM': epic.sgm ? 'TRUE' : 'FALSE',
        'Strategic Context': epic.notes || ''
    }));

    const ws = XLSX.utils.json_to_sheet(data, { header: [...TEMPLATE_COLUMNS] });

    // Set column widths
    ws['!cols'] = [
        { wch: 40 }, { wch: 25 }, { wch: 12 }, { wch: 10 },
        { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
        { wch: 12 }, { wch: 10 }, { wch: 8 }, { wch: 50 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Epics');

    const timestamp = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `epics_export_${timestamp}.xlsx`);
}

/**
 * Parse uploaded Excel file
 */
export function parseExcelFile(file: File, projects: Project[]): Promise<ParsedImportResult> {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(firstSheet);

                const rows: ImportRow[] = [];
                const errors: ValidationError[] = [];

                jsonData.forEach((row: any, index: number) => {
                    const rowNum = index + 2; // Excel row (1-indexed + header)

                    // Validate Title
                    const title = String(row['Title'] || '').trim();
                    if (!title) {
                        errors.push({ row: rowNum, column: 'Title', message: 'Title is required' });
                    }

                    // Validate Project
                    const projectName = String(row['Project'] || '').trim();
                    const project = projects.find(p => p.name.toLowerCase() === projectName.toLowerCase());
                    if (!projectName) {
                        errors.push({ row: rowNum, column: 'Project', message: 'Project is required' });
                    } else if (!project) {
                        errors.push({ row: rowNum, column: 'Project', message: `Project "${projectName}" not found` });
                    }

                    // Validate Lane
                    const laneInput = String(row['Lane'] || 'Unassigned').trim();
                    const lane = VALID_LANES.find(l => l.toLowerCase() === laneInput.toLowerCase()) || 'Unassigned';

                    // Validate scoring fields (1-5)
                    const validateScore = (value: any, column: string): number => {
                        const num = Number(value);
                        if (isNaN(num) || num < 1 || num > 5) {
                            if (value !== undefined && value !== '') {
                                errors.push({ row: rowNum, column, message: `Must be 1-5` });
                            }
                            return 3; // default
                        }
                        return Math.round(num);
                    };

                    const security = validateScore(row['Security'], 'Security');
                    const business = validateScore(row['Business Value'], 'Business Value');
                    const time = validateScore(row['Time Criticality'], 'Time Criticality');
                    const ops = validateScore(row['Ops Impact'], 'Ops Impact');
                    const confidence = validateScore(row['Confidence'], 'Confidence');

                    // Validate Effort (1-5)
                    let effort: 1 | 2 | 3 | 4 | 5 = 3;
                    const effortNum = Number(row['Effort']);
                    if (VALID_EFFORTS.includes(effortNum)) {
                        effort = effortNum as 1 | 2 | 3 | 4 | 5;
                    } else if (row['Effort'] !== undefined && row['Effort'] !== '') {
                        errors.push({ row: rowNum, column: 'Effort', message: 'Must be 1-5' });
                    }

                    // Parse SGM
                    const sgmValue = String(row['SGM'] || '').toUpperCase();
                    const sgm = sgmValue === 'TRUE' || sgmValue === 'YES' || sgmValue === '1';

                    // Parse Progress (0-100)
                    let progress = 0;
                    const progressNum = Number(row['Progress']);
                    if (!isNaN(progressNum) && progressNum >= 0 && progressNum <= 100) {
                        progress = Math.round(progressNum);
                    }

                    // Strategic Context
                    const notes = String(row['Strategic Context'] || '').trim();

                    rows.push({
                        title,
                        projectName,
                        lane,
                        progress,
                        security,
                        business,
                        time,
                        ops,
                        confidence,
                        effort,
                        sgm,
                        notes
                    });
                });

                resolve({
                    rows,
                    errors,
                    isValid: errors.length === 0 && rows.length > 0
                });
            } catch (err) {
                resolve({
                    rows: [],
                    errors: [{ row: 0, column: '', message: 'Failed to parse Excel file' }],
                    isValid: false
                });
            }
        };
        reader.readAsArrayBuffer(file);
    });
}

/**
 * Convert parsed rows to epic payloads for API
 */
export function convertToEpicPayloads(rows: ImportRow[], projects: Project[]): Partial<Epic>[] {
    return rows
        .filter(row => row.title && row.projectName)
        .map(row => {
            const project = projects.find(p => p.name.toLowerCase() === row.projectName.toLowerCase());
            return {
                title: row.title,
                projectId: project?.id || '',
                lane: row.lane,
                progress: row.progress,
                completedAt: row.progress === 100 ? new Date().toISOString() : undefined,
                security: row.security,
                business: row.business,
                time: row.time,
                ops: row.ops,
                confidence: row.confidence,
                effort: row.effort,
                sgm: row.sgm,
                notes: row.notes,
                so: [],
                arq: []
            };
        })
        .filter(e => e.projectId);
}
